import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Grid,
    TextField,
} from "@mui/material";
import { Formik } from "formik";
import { useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import DefaultApiService from "../../../../../infrastructure/api/default";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import DropdownPicker from "../../../../components/shared/dropdown";
import JalaliDateTimePicker from "../../../../components/shared/jalali-datetimepicker";

const validationSchema = Yup.object().shape({
  substationName: Yup.string().required("لطفا این فیلد را پر کنید."),
  substationLatinName: Yup.string().required("لطفا این فیلد را پر کنید."),
  substationDispatchingCode: Yup.string().required("لطفا این فیلد را پر کنید."),
  substationIGMCCode: Yup.string().required("لطفا این فیلد را پر کنید."),
  substationGeoCode: Yup.string().required("لطفا این فیلد را پر کنید."),
  substationGISCode: Yup.string().required("لطفا این فیلد را پر کنید."),
  substationTotalCapacity: Yup.string().required("لطفا این فیلد را پر کنید."),
  percentInOutDiff: Yup.number().required("لطفا این فیلد را پر کنید."),
  substationKind: Yup.string().required("لطفا این فیلد را پر کنید."),
  companyId: Yup.number().required("لطفا این فیلد را پر کنید."),
  cityId: Yup.number().required("لطفا این فیلد را پر کنید."),
  substationActDate: Yup.string().required("لطفا این فیلد را پر کنید."),
  substationDismandDate: Yup.string().nullable(),
});

const SubstationModifyPage = () => {
  const data = useLoaderData("root");
  let navigate = useNavigate();
  const { id: roleId } = useParams();

  const [loading, setLoading] = useState(false);

  const onNavigateToListPage = () => {
    navigate("/BI/Grid/SubstationInformation");
  };

  const onFilterByCompanyId = (list, id) => {
    return list.filter((x) => x.companyId === id);
  };

  // Submit Action
  const onSubmit = async (formValue, { setSubmitting }) => {
    setLoading(true);
    const sendData = { ...data.substation, ...formValue };

    try {
      const res = await DefaultApiService.modifyApi(
        "BI/Grid/SubstationInformation",
        sendData
      );

      if (res.isSuccess) {
        toast.success(res.message);
        onNavigateToListPage();
      } else {
        toast.error(res.message);
      }

      setLoading(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");

      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={
              roleId !== "create"
                ? `ویرایش ${data?.substation?.substationName}`
                : "ایجاد"
            }
          />

          <CardContent>
            <Formik
              initialValues={data.substation}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => {
                const onMakeFormikVariable = (name) => ({
                  name: name,
                  fullWidth: true,
                  value: values[name],
                  error: errors[name] && touched[name],
                  helperText: errors[name] && touched[name] && errors[name],
                  variant: "outlined",
                });

                return (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="نام ایستگاه"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("substationName")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="نام لاتین ایستگاه"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("substationLatinName")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="کد دیسپاچینگ"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("substationDispatchingCode")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="شناسه ایستگاه"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("igmcStationId")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="کد IGMC"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("substationIGMCCode")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="کد GIS"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("substationGeoCode")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="کد دوم GIS"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("substationGISCode")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <DropdownPicker
                          label="نوع ایستگاه"
                          onChange={(event) => {
                            setFieldValue("substationKind", event.target.value);
                          }}
                          list={data.substationKind}
                          {...onMakeFormikVariable("substationKind")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <DropdownPicker
                          label="مالکیت ایستگاه"
                          onChange={(event) => {
                            setFieldValue("companyId", event.target.value);
                            setFieldValue("cityId", "");
                          }}
                          list={data.companyList}
                          optionName="companyName"
                          optionValue="id"
                          {...onMakeFormikVariable("companyId")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <DropdownPicker
                          label="شهرستان"
                          onChange={(event) => {
                            setFieldValue("cityId", event.target.value);
                          }}
                          list={onFilterByCompanyId(
                            data.cityList,
                            values.companyId
                          )}
                          optionName="cityName"
                          optionValue="id"
                          {...onMakeFormikVariable("cityId")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="ظرفیت"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("substationTotalCapacity")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="درصد مجاز اختلاف ورودی و خروجی"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("percentInOutDiff")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <JalaliDateTimePicker
                          label="تاریخ شروع بهره برداری"
                          onChange={(value) => {
                            setFieldValue("substationActDate", value);
                          }}
                          {...onMakeFormikVariable("substationActDate")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <JalaliDateTimePicker
                          label="تاریخ پایان بهره برداری"
                          onChange={(value) => {
                            setFieldValue("substationDismandDate", value);
                          }}
                          {...onMakeFormikVariable("substationDismandDate")}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={12}
                        container
                        justifyContent={"flex-end"}
                      >
                        <Button
                          disabled={isSubmitting}
                          variant="outlined"
                          size="large"
                          color="gray"
                          sx={{ mx: 1 }}
                          onClick={onNavigateToListPage}
                        >
                          انصراف
                        </Button>

                        <Button
                          disabled={isSubmitting}
                          variant="contained"
                          type="submit"
                          size="large"
                        >
                          {loading && (
                            <CircularProgress size={16} color="inherit" />
                          )}
                          {roleId !== "create" ? "ویرایش" : "ایجاد"}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default SubstationModifyPage;
